import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BizPagination from '../../Core/Components/Mini/BizPagination';
import { BizButton } from '../../Core/Components/Mini/BizButton';
import {
	useAddExternalTicketComment,
	useCloseExternalTicket,
	useCreateExternalTicket,
	useExternalTicketDetail,
	useExternalTicketList,
	useExternalTicketWorkspaceOptions,
	useSubmitExternalTicket,
	useUpdateAdminExternalTicket,
	useUpdateMyExternalTicket,
	useExternalTicketAttachments,
	useUploadExternalTicketAttachment,
} from '../../Core/Hooks/useExternalTickets';
import {
	ExternalTicket,
	ExternalTicketAdminFilters,
	ExternalTicketAdminOptions,
	ExternalTicketDraft,
	ExternalTicketOptions,
} from '../../Core/Types/externalTicket';
import './ExternalTicketPortal.scss';

type Props = {
	admin?: boolean;
	initialTicketId?: number;
	initialCreate?: boolean;
};

const pageSize = 20;
const maxAttachmentSizeBytes = 200 * 1024 * 1024;
const acceptedAttachmentExtensions = [
	'.jpg', '.jpeg', '.png', '.svg', '.gif',
	'.mp4',
	'.doc', '.docx',
	'.xls', '.xlsx',
	'.pdf',
];
const acceptedAttachmentTypes = acceptedAttachmentExtensions.join(',');

const priorityTranslationKeys: Record<number, string> = {
	13: 'EXTERNAL_TICKETS.PRIORITIES.CRITICAL',
	9: 'EXTERNAL_TICKETS.PRIORITIES.HIGH',
	4: 'EXTERNAL_TICKETS.PRIORITIES.NORMAL',
	5: 'EXTERNAL_TICKETS.PRIORITIES.LOW',
	6: 'EXTERNAL_TICKETS.PRIORITIES.FUTURE',
	12: 'EXTERNAL_TICKETS.PRIORITIES.PRIVATE',
};

const getPriorityLabel = (id: number, fallbackName: string, translate: (key: string) => string) => {
	const translationKey = priorityTranslationKeys[id];
	return translationKey ? translate(translationKey) : fallbackName;
};

const getFileExtension = (fileName: string) => {
	const lastDot = fileName.lastIndexOf('.');
	return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : '';
};

const ExternalTicketPortal = ({ admin = false, initialTicketId = 0, initialCreate = false }: Props) => {
	const { t, i18n } = useTranslation();
	const [search, setSearch] = useState('');
	const [projectFilterId, setProjectFilterId] = useState(0);
	const [statusFilterId, setStatusFilterId] = useState(0);
	const [priorityFilterId, setPriorityFilterId] = useState(0);
	const [page, setPage] = useState(1);
	const [selectedTicketId, setSelectedTicketId] = useState(initialTicketId);
	const [creating, setCreating] = useState(initialCreate);
	const [scrollRequest, setScrollRequest] = useState(0);
	const adminFilters = useMemo<ExternalTicketAdminFilters>(() => ({
		projectId: projectFilterId,
		statusId: statusFilterId,
		priorityId: priorityFilterId,
	}), [projectFilterId, statusFilterId, priorityFilterId]);
	const list = useExternalTicketList(admin, search, page, adminFilters, pageSize);
	const detail = useExternalTicketDetail(admin, selectedTicketId);
	const optionsQuery = useExternalTicketWorkspaceOptions(admin);
	const adminOptions = admin && optionsQuery.data ? optionsQuery.data as ExternalTicketAdminOptions : undefined;

	const openTicket = (id: number) => {
		setCreating(false);
		setSelectedTicketId(id);
	};

	const startCreate = () => {
		setScrollRequest(0);
		setSelectedTicketId(0);
		setCreating(true);
	};

	const closeEditor = () => {
		setScrollRequest(0);
		setSelectedTicketId(0);
		setCreating(false);
	};

	return (
		<div className={`biz-external-tickets${admin ? ' biz-external-tickets--admin' : ''}`}>
			<div className='biz-external-tickets__header'>
				<div>
					<h2>{t(admin ? 'EXTERNAL_TICKETS.HEADER.ADMIN_TITLE' : 'EXTERNAL_TICKETS.HEADER.USER_TITLE')}</h2>
					<p>{t(admin ? 'EXTERNAL_TICKETS.HEADER.ADMIN_DESCRIPTION' : 'EXTERNAL_TICKETS.HEADER.USER_DESCRIPTION')}</p>
				</div>
				<BizButton text={t('EXTERNAL_TICKETS.ACTIONS.NEW_TICKET')} icon='fa-solid fa-plus' isLead onClick={startCreate} legacy={admin} />
			</div>

			{(creating || selectedTicketId > 0) && (
				<TicketEditor
					admin={admin}
					scrollRequest={scrollRequest}
					creating={creating}
					ticketId={selectedTicketId}
					ticket={detail.data}
					options={optionsQuery.data}
					loading={selectedTicketId > 0 && detail.statusState === 'LOADING'}
					loadError={detail.errorMessage}
					onCreated={openTicket}
					onClose={closeEditor}
				/>
			)}

			<section className='biz-external-tickets__list' aria-label={t(admin ? 'EXTERNAL_TICKETS.A11Y.CUSTOMER_TICKETS' : 'EXTERNAL_TICKETS.A11Y.MY_TICKETS')}>
				<div className={`biz-external-tickets__filters${admin ? ' biz-external-tickets__filters--admin' : ''}`}>
					<div className='biz-external-tickets__filter'>
						<label htmlFor={`external-ticket-search-${admin ? 'admin' : 'user'}`}>{t('EXTERNAL_TICKETS.FIELDS.SEARCH')}</label>
						<input
							id={`external-ticket-search-${admin ? 'admin' : 'user'}`}
							type='search'
							value={search}
							onChange={(event) => {
								setSearch(event.target.value);
								setPage(1);
							}}
							placeholder={t('EXTERNAL_TICKETS.PLACEHOLDERS.SEARCH')}
						/>
					</div>
					{admin && (
						<>
							<div className='biz-external-tickets__filter'>
								<label htmlFor='external-ticket-project-filter'>{t('EXTERNAL_TICKETS.FIELDS.PROJECT')}</label>
								<select id='external-ticket-project-filter' value={projectFilterId} onChange={(event) => { setProjectFilterId(Number(event.target.value)); setPage(1); }}>
									<option value={0}>{t('EXTERNAL_TICKETS.FILTERS.ALL_PROJECTS')}</option>
									{adminOptions?.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
								</select>
							</div>
							<div className='biz-external-tickets__filter'>
								<label htmlFor='external-ticket-status-filter'>{t('EXTERNAL_TICKETS.FIELDS.STATUS')}</label>
								<select id='external-ticket-status-filter' value={statusFilterId} onChange={(event) => { setStatusFilterId(Number(event.target.value)); setPage(1); }}>
									<option value={0}>{t('EXTERNAL_TICKETS.FILTERS.ALL_STATUSES')}</option>
									{optionsQuery.data && <option value={optionsQuery.data.draftStatusId}>{t('EXTERNAL_TICKETS.STATUS.NEW')}</option>}
									<option value={-1}>{t('EXTERNAL_TICKETS.STATUS.SUBMITTED')}</option>
									{optionsQuery.data && optionsQuery.data.awaitingCustomerStatusId > 0 && <option value={optionsQuery.data.awaitingCustomerStatusId}>{optionsQuery.data.awaitingCustomerStatusName}</option>}
									{optionsQuery.data && <option value={optionsQuery.data.closedStatusId}>{t('EXTERNAL_TICKETS.STATUS.CLOSED')}</option>}
								</select>
							</div>
							<div className='biz-external-tickets__filter'>
								<label htmlFor='external-ticket-priority-filter'>{t('EXTERNAL_TICKETS.FIELDS.PRIORITY')}</label>
								<select id='external-ticket-priority-filter' value={priorityFilterId} onChange={(event) => { setPriorityFilterId(Number(event.target.value)); setPage(1); }}>
									<option value={0}>{t('EXTERNAL_TICKETS.FILTERS.ALL_PRIORITIES')}</option>
									{adminOptions?.priorities.map((priority) => <option key={priority.id} value={priority.id}>{getPriorityLabel(priority.id, priority.name, t)}</option>)}
								</select>
							</div>
						</>
					)}
				</div>
				{list.statusState === 'LOADING' && <div className='biz-external-tickets__message'>{t('EXTERNAL_TICKETS.STATES.LOADING_TICKETS')}</div>}
				{list.errorMessage && <div className='biz-external-tickets__message biz-external-tickets__message--error'>{list.errorMessage}</div>}
				{list.statusState === 'SUCCESS' && list.data?.items.length === 0 && (
					<div className='biz-external-tickets__empty'>
						<i className='fa-light fa-ticket' aria-hidden='true' />
						<strong>{t('EXTERNAL_TICKETS.STATES.NO_TICKETS')}</strong>
						<span>{t('EXTERNAL_TICKETS.STATES.NO_TICKETS_DESCRIPTION')}</span>
					</div>
				)}
				<div className='biz-external-tickets__cards'>
					{list.data?.items.map((ticket) => (
						<button type='button' className='biz-external-tickets__card' key={ticket.id} onClick={() => { openTicket(ticket.id); setScrollRequest((request) => request + 1); }}>
							<div className='biz-external-tickets__card-main'>
								<strong>{ticket.name}</strong>
								{admin && <span>{ticket.reporterName || t('EXTERNAL_TICKETS.LABELS.UNKNOWN_REPORTER')}</span>}
								<small>{t('EXTERNAL_TICKETS.DATES.UPDATED', { date: formatDate(ticket.updatedAt, i18n.resolvedLanguage) })}</small>
							</div>
							<div className='biz-external-tickets__badges'>
								<TicketBadge text={getPriorityLabel(ticket.priorityId, ticket.priorityName, t)} colour={ticket.priorityColour} />
								<TicketBadge text={ticket.isAwaitingCustomer ? ticket.statusName : t(getStatusLabelKey(ticket))} tone={getStatusTone(ticket)} />
							</div>
						</button>
					))}
				</div>
				{list.data && list.data.totalCount > pageSize && (
					<BizPagination
						pageIndex={page - 1}
						pageSize={pageSize}
						currentPageLength={list.data.items.length}
						totalItemCount={list.data.totalCount}
						pageIndexChanged={(index) => setPage(index + 1)}
					/>
				)}
			</section>
		</div>
	);
};

type EditorProps = {
	scrollRequest: number;
	admin: boolean;
	creating: boolean;
	ticketId: number;
	ticket: ReturnType<typeof useExternalTicketDetail>['data'];
	options: ExternalTicketOptions | ExternalTicketAdminOptions | null | undefined;
	loading: boolean;
	loadError: string | null;
	onCreated: (id: number) => void;
	onClose: () => void;
};

const TicketEditor = ({ scrollRequest, admin, creating, ticketId, ticket, options, loading, loadError, onCreated, onClose }: EditorProps) => {
	const { t, i18n } = useTranslation();
	const editorRef = useRef<HTMLElement>(null);
	const lastScrollRequest = useRef(0);
	useEffect(() => {
		if (scrollRequest === 0) {
			lastScrollRequest.current = 0;
			return;
		}
		if (loading || scrollRequest === lastScrollRequest.current) return;
		lastScrollRequest.current = scrollRequest;
		editorRef.current?.scrollIntoView({
			behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
			block: 'start',
		});
	}, [scrollRequest, loading]);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [priorityId, setPriorityId] = useState(0);
	const [projectId, setProjectId] = useState(0);
	const [milestoneId, setMilestoneId] = useState<number | undefined>();
	const [comment, setComment] = useState('');
	const create = useCreateExternalTicket(onCreated);
	const updateMine = useUpdateMyExternalTicket();
	const updateAdmin = useUpdateAdminExternalTicket();
	const addComment = useAddExternalTicketComment(admin);
	const submit = useSubmitExternalTicket();
	const closeTicket = useCloseExternalTicket();
	const adminOptions = options && 'projects' in options ? options : undefined;
	const priorityAllowed = options?.priorities.some((priority) => priority.id === priorityId) === true;
	const currentPriorityIsUnavailable = ticket != null && options != null && !options.priorities.some((priority) => priority.id === ticket.priorityId);
	const milestones = useMemo(
		() => adminOptions?.projects.find((project) => project.id === projectId)?.milestones ?? [],
		[adminOptions, projectId]
	);

	useEffect(() => {
		if (ticket) {
			setName(ticket.name);
			setDescription(ticket.description || '');
			setPriorityId(ticket.priorityId);
			setProjectId(adminOptions && !adminOptions.projects.some((project) => project.id === ticket.projectId) ? 0 : ticket.projectId);
			setMilestoneId(ticket.milestoneId);
		} else if (creating && options) {
			setName('');
			setDescription('');
			setPriorityId(options.defaultPriorityId);
			setProjectId(adminOptions?.defaultProjectId ?? 0);
			setMilestoneId(undefined);
		}
	}, [ticket?.id, creating, options?.defaultPriorityId, adminOptions?.defaultProjectId, adminOptions?.projects]);

	const selectedMilestoneId = milestones.some((milestone) => milestone.id === milestoneId) ? milestoneId : undefined;
	const editable = creating || ticket?.isDraft === true;
	const canSubmit = admin && !creating && (ticket?.isDraft === true || ticket?.isAwaitingCustomer === true);
	const save = async () => {
		const draft: ExternalTicketDraft = { name: name.trim(), description, priorityId };
		if (creating) {
			await create.mutateAsync(draft);
		} else if (admin) {
			await updateAdmin.mutateAsync({ id: ticketId, draft: { ...draft, projectId, milestoneId: selectedMilestoneId } });
		} else {
			await updateMine.mutateAsync({ id: ticketId, draft });
		}
	};

	const sendComment = async () => {
		await addComment.mutateAsync({ id: ticketId, text: comment.trim() });
		setComment('');
	};

	if (loading) return <section ref={editorRef} className='biz-external-tickets__editor'>{t('EXTERNAL_TICKETS.STATES.LOADING_TICKET')}</section>;

	return (
		<section ref={editorRef} className='biz-external-tickets__editor' aria-label={t(creating ? 'EXTERNAL_TICKETS.A11Y.CREATE_TICKET' : 'EXTERNAL_TICKETS.A11Y.TICKET_DETAILS')}>
			<div className='biz-external-tickets__editor-title'>
				<h3>{creating ? t('EXTERNAL_TICKETS.ACTIONS.CREATE_TICKET') : t('EXTERNAL_TICKETS.LABELS.TICKET_NUMBER', { id: ticketId })}</h3>
				<button type='button' aria-label={t('EXTERNAL_TICKETS.A11Y.CLOSE_EDITOR')} onClick={onClose}><i className='far fa-times' /></button>
			</div>
			{loadError && <div className='biz-external-tickets__message biz-external-tickets__message--error'>{loadError}</div>}
			{ticket && (
				<div className='biz-external-tickets__summary'>
					<TicketBadge text={ticket.isAwaitingCustomer ? ticket.statusName : t(getStatusLabelKey(ticket))} tone={getStatusTone(ticket)} />
					<span>{t('EXTERNAL_TICKETS.DATES.CREATED', { date: formatDate(ticket.createdAt, i18n.resolvedLanguage) })}</span>
					{admin && <span>{t('EXTERNAL_TICKETS.LABELS.REPORTED_BY', { name: ticket.reporterName || t('EXTERNAL_TICKETS.LABELS.UNKNOWN_REPORTER') })}</span>}
				</div>
			)}

			<div className='biz-external-tickets__fields'>
				<label className='biz-external-tickets__field biz-external-tickets__field--wide'>
					<span>{t('EXTERNAL_TICKETS.FIELDS.TITLE')}</span>
					<input value={name} maxLength={1000} readOnly={!editable} onChange={(event) => setName(event.target.value)} />
				</label>
				<label className='biz-external-tickets__field biz-external-tickets__field--wide'>
					<span>{t('EXTERNAL_TICKETS.FIELDS.DESCRIPTION')}</span>
					<textarea value={description} rows={7} readOnly={!editable} onChange={(event) => setDescription(event.target.value)} />
				</label>
				<label className='biz-external-tickets__field'>
					<span>{t('EXTERNAL_TICKETS.FIELDS.PRIORITY')}</span>
					<select value={priorityId} disabled={!editable} onChange={(event) => setPriorityId(Number(event.target.value))}>
						<option value={0}>{t('EXTERNAL_TICKETS.PLACEHOLDERS.SELECT_PRIORITY')}</option>
						{ticket && currentPriorityIsUnavailable && <option value={ticket.priorityId} disabled>{getPriorityLabel(ticket.priorityId, ticket.priorityName, t)}</option>}
						{options?.priorities.map((priority) => <option key={priority.id} value={priority.id}>{getPriorityLabel(priority.id, priority.name, t)}</option>)}
					</select>
					{editable && currentPriorityIsUnavailable && !priorityAllowed && <small>{t('EXTERNAL_TICKETS.STATES.PRIORITY_UNAVAILABLE')}</small>}
				</label>
				{admin && !creating && (
					<>
						<label className='biz-external-tickets__field'>
							<span>{t('EXTERNAL_TICKETS.FIELDS.PROJECT')}</span>
							<select value={projectId} disabled={!editable} onChange={(event) => { setProjectId(Number(event.target.value)); setMilestoneId(undefined); }}>
								<option value={0}>{t('EXTERNAL_TICKETS.PLACEHOLDERS.SELECT_PROJECT')}</option>
								{adminOptions?.projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
							</select>
						</label>
						{milestones.length > 0 && (
							<label className='biz-external-tickets__field'>
								<span>{t('EXTERNAL_TICKETS.FIELDS.PHASE')}</span>
								<select value={selectedMilestoneId ?? 0} disabled={!editable || projectId <= 0} onChange={(event) => setMilestoneId(Number(event.target.value) || undefined)}>
									<option value={0}>{t('EXTERNAL_TICKETS.PLACEHOLDERS.NO_PHASE')}</option>
									{milestones.map((phase) => <option key={phase.id} value={phase.id}>{phase.name}</option>)}
								</select>
							</label>
						)}
					</>
				)}
			</div>

			{(editable || canSubmit) && (
				<div className='biz-external-tickets__actions'>
					<div className='biz-external-tickets__actions-primary'>
						{editable && <BizButton
							text={t(creating ? 'EXTERNAL_TICKETS.ACTIONS.CREATE_TICKET' : 'EXTERNAL_TICKETS.ACTIONS.SAVE_CHANGES')}
							icon='far fa-save'
							isLead
							legacy={admin}
							state={creating ? create.statusState : admin ? updateAdmin.statusState : updateMine.statusState}
							disabled={!name.trim() || !priorityAllowed || (admin && !creating && projectId <= 0)}
							onClick={() => void save()}
						/>}
						{canSubmit && (
							<BizButton
								text={t('EXTERNAL_TICKETS.ACTIONS.SUBMIT_FOR_REVIEW')}
								icon='far fa-check'
								legacy
								state={submit.statusState}
								disabled={!adminOptions?.projects.some((project) => project.id === projectId)}
								onClick={() => void submit.mutateAsync({ id: ticketId, projectId, milestoneId: selectedMilestoneId })}
							/>
						)}
					</div>
					{admin && !creating && (ticket?.isDraft === true || ticket?.isAwaitingCustomer === true) && (
						<div className='biz-external-tickets__actions-close'>
							<BizButton
								text={t('EXTERNAL_TICKETS.ACTIONS.CLOSE_TICKET')}
								icon='far fa-times'
								legacy
								className='biz-external-tickets__close-button'
								state={closeTicket.statusState}
								onClick={() => void closeTicket.mutateAsync({ id: ticketId })}
							/>
						</div>
					)}
				</div>
			)}
			{!creating && !editable && <p className='biz-external-tickets__locked'>{t(admin && ticket?.isAwaitingCustomer ? 'EXTERNAL_TICKETS.STATES.AWAITING_CUSTOMER' : 'EXTERNAL_TICKETS.STATES.SUBMITTED_LOCKED')}</p>}
			<MutationError messages={[create.errorMessage, updateMine.errorMessage, updateAdmin.errorMessage, submit.errorMessage, closeTicket.errorMessage]} />

			<AttachmentsPanel
				admin={admin}
				ticketId={ticketId}
				creating={creating}
				canUpload={!creating && (ticket?.isDraft === true || (admin && ticket?.isAwaitingCustomer === true))}
				enabled={options?.attachmentsEnabled === true}
			/>

			{!creating && ticket && (
				<section className='biz-external-tickets__comments'>
					<h4>{t('EXTERNAL_TICKETS.COMMENTS.TITLE')}</h4>
					{ticket.comments.length === 0 && <p className='biz-external-tickets__muted'>{t('EXTERNAL_TICKETS.COMMENTS.EMPTY')}</p>}
					{ticket.comments.map((item) => (
						<article key={item.id} className='biz-external-tickets__comment'>
							<div><strong>{item.userName || t('EXTERNAL_TICKETS.COMMENTS.SUPPORT')}</strong><time>{formatDate(item.date, i18n.resolvedLanguage)}</time></div>
							<p>{item.text}</p>
						</article>
					))}
					{!ticket.isClosed ? (
						<div className='biz-external-tickets__comment-form'>
							<label htmlFor={`ticket-comment-${ticketId}`}>{t('EXTERNAL_TICKETS.COMMENTS.ADD')}</label>
							<textarea id={`ticket-comment-${ticketId}`} rows={4} maxLength={8000} value={comment} onChange={(event) => setComment(event.target.value)} />
							<BizButton text={t('EXTERNAL_TICKETS.ACTIONS.SEND_COMMENT')} icon='fa-solid fa-comment' legacy={admin} state={addComment.statusState} disabled={!comment.trim()} onClick={() => void sendComment()} />
							<MutationError messages={[addComment.errorMessage]} />
						</div>
					) : <p className='biz-external-tickets__locked'>{t('EXTERNAL_TICKETS.COMMENTS.CLOSED')}</p>}
				</section>
			)}
		</section>
	);
};

type AttachmentsPanelProps = {
	admin: boolean;
	ticketId: number;
	creating: boolean;
	canUpload: boolean;
	enabled: boolean;
};

const AttachmentsPanel = ({ admin, ticketId, creating, canUpload, enabled }: AttachmentsPanelProps) => {
	const { t, i18n } = useTranslation();
	const attachments = useExternalTicketAttachments(admin, ticketId, enabled && !creating);
	const upload = useUploadExternalTicketAttachment(admin);
	const [validationError, setValidationError] = useState('');
	const uploadFiles = async (files: FileList | null) => {
		if (!files || !canUpload) return;
		const selectedFiles = Array.from(files);
		const unsupportedFile = selectedFiles.find((file) => !acceptedAttachmentExtensions.includes(getFileExtension(file.name)));
		if (unsupportedFile) {
			setValidationError(t('EXTERNAL_TICKETS.ATTACHMENTS.INVALID_TYPE', { name: unsupportedFile.name }));
			return;
		}
		const oversizedFile = selectedFiles.find((file) => file.size > maxAttachmentSizeBytes);
		if (oversizedFile) {
			setValidationError(t('EXTERNAL_TICKETS.ATTACHMENTS.TOO_LARGE', { name: oversizedFile.name }));
			return;
		}
		setValidationError('');
		for (const file of selectedFiles) {
			await upload.mutateAsync({ id: ticketId, file });
		}
	};

	let helpText = t('EXTERNAL_TICKETS.ATTACHMENTS.DRAFT_HELP');
	if (!enabled) helpText = t('EXTERNAL_TICKETS.ATTACHMENTS.NOT_CONFIGURED');
	else if (creating) helpText = t('EXTERNAL_TICKETS.ATTACHMENTS.CREATE_FIRST');
	else if (!canUpload) helpText = t('EXTERNAL_TICKETS.ATTACHMENTS.SUBMITTED_HELP');

	return (
		<section className='biz-external-tickets__attachments' aria-label={t('EXTERNAL_TICKETS.ATTACHMENTS.TITLE')}>
			<div className='biz-external-tickets__attachment-header'>
				<div><h4>{t('EXTERNAL_TICKETS.ATTACHMENTS.TITLE')}</h4><span>{helpText} {t('EXTERNAL_TICKETS.ATTACHMENTS.RESTRICTIONS')}</span></div>
				<label className={`biz-external-tickets__attachment-upload${!enabled || !canUpload ? ' is-disabled' : ''}`}>
					<i className='far fa-upload' /> {t(upload.statusState === 'LOADING' ? 'EXTERNAL_TICKETS.ATTACHMENTS.UPLOADING' : 'EXTERNAL_TICKETS.ATTACHMENTS.ADD_FILES')}
					<input
						type='file'
						multiple
						accept={acceptedAttachmentTypes}
						disabled={!enabled || !canUpload || upload.statusState === 'LOADING'}
						onChange={(event) => {
							const input = event.currentTarget;
							void uploadFiles(input.files).catch(() => undefined).finally(() => { input.value = ''; });
						}}
					/>
				</label>
			</div>
			{attachments.statusState === 'LOADING' && <div className='biz-external-tickets__attachment-empty'>{t('EXTERNAL_TICKETS.ATTACHMENTS.LOADING')}</div>}
			{attachments.errorMessage && <div className='biz-external-tickets__message biz-external-tickets__message--error'>{attachments.errorMessage}</div>}
			{validationError && <div className='biz-external-tickets__message biz-external-tickets__message--error'>{validationError}</div>}
			{upload.errorMessage && <div className='biz-external-tickets__message biz-external-tickets__message--error'>{upload.errorMessage}</div>}
			{attachments.statusState === 'SUCCESS' && attachments.data?.length === 0 && (
				<div className='biz-external-tickets__attachment-empty'>{t('EXTERNAL_TICKETS.ATTACHMENTS.EMPTY')}</div>
			)}
			{attachments.data && attachments.data.length > 0 && (
				<div className='biz-external-tickets__attachment-list'>
					{attachments.data.map((file) => (
						<div className='biz-external-tickets__attachment-file' key={file.id}>
							<div><i className='far fa-file' /><span>{file.name}</span><small>{formatDate(file.createdAt, i18n.resolvedLanguage)}</small></div>
							{file.url ? (
								<a href={file.url} target='_blank' rel='noreferrer'><i className='far fa-download' /> {t('EXTERNAL_TICKETS.ATTACHMENTS.DOWNLOAD')}</a>
							) : <button type='button' disabled><i className='far fa-download' /> {t('EXTERNAL_TICKETS.ATTACHMENTS.DOWNLOAD')}</button>}
						</div>
					))}
				</div>
			)}
		</section>
	);
};

type TicketBadgeTone = 'draft' | 'submitted' | 'closed';

const TicketBadge = ({ text, colour, tone }: { text: string; colour?: string; tone?: TicketBadgeTone }) => (
	<span
		className={`biz-external-tickets__badge${tone ? ` biz-external-tickets__badge--${tone}` : ''}`}
		style={!tone && colour ? { borderColor: colour, color: colour } : undefined}
	>
		{text}
	</span>
);

const getStatusTone = (ticket: Pick<ExternalTicket, 'isDraft' | 'isClosed'>): TicketBadgeTone => {
	if (ticket.isDraft) return 'draft';
	if (ticket.isClosed) return 'closed';
	return 'submitted';
};

const getStatusLabelKey = (ticket: Pick<ExternalTicket, 'isDraft' | 'isClosed'>) => {
	if (ticket.isDraft) return 'EXTERNAL_TICKETS.STATUS.NEW';
	if (ticket.isClosed) return 'EXTERNAL_TICKETS.STATUS.CLOSED';
	return 'EXTERNAL_TICKETS.STATUS.SUBMITTED';
};

const MutationError = ({ messages }: { messages: Array<string | null> }) => {
	const message = messages.find(Boolean);
	return message ? <div className='biz-external-tickets__message biz-external-tickets__message--error'>{message}</div> : null;
};

const formatDate = (value: string, locale?: string) => {
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(locale);
};

export default ExternalTicketPortal;
